<template>
  <div>
    <LayoutContent>
      <SectionTitle :label="$t('label.transferToBank')" />
      <v-card class="rounded-lg overflow-hidden elevation-3">
        <v-form ref="transferForm" v-if="currentAgent">
          <div class="coin-conversion--container">
            <div class="d-flex flex-column justify-center pa-8 primary lighten-1 white--text">
              <h4 class="text-center text-h5 font-weight-bold">{{ $t('label.availableCashBalance') }}</h4>
              <p class="text-center text-h6 font-weight-medium">{{ currentAgent.currency }} {{ currentAgent.total_cashback }}</p>
            </div>
            <div class="pa-8">
              <p class="font-weight-bold text-subtitle-1">{{ $t('label.transferAmount') }}</p>
              <v-text-field
                v-model="form.amount"
                placeholder="0"
                color="primary lighten-1"
                class="d-block mb-auto shrink primary--input text-h5"
                :rules="$rules.requiredRules"
                :prefix="currentAgent.currency"
                hide-details="auto"
                single-line
              />
              <div class="text-subtitle-2 text-right">{{ $t('label.minimumTransferAmt', { currency: currentAgent.currency, amount: agent_min_withdrawal_amount.toFixed(2) }) }}</div>
            </div>
            <div class="coin-options--wrapper pa-8">
              <v-btn
                v-for="amount in presetCashAmounts"
                depressed
                text
                outlined
                color="primary darken-1"
                class="coin-option font-weight-bold"
                :key="amount"
                @click="form.amount = amount"
                :disabled="amount > currentAgent.total_cashback"
              >
                {{ amount }}
              </v-btn>
            </div>
          </div>
          <div class="py-6">
            <section class="bank-account-details pa-8 mx-auto" :class="$vuetify.breakpoint.smAndUp ? 'px-16' : 'px-4'">
              <SectionTitle :label="$t('label.bankAccountDetails')" />
              <div class="bank-fields--wrapper">
                <fieldset>
                  <label for="form.bank_id" class="font-weight-bold">{{ $t('label.bankName') }}</label>
                  <v-select
                    v-model="form.bank_id"
                    :items="bankList"
                    item-value="id"
                    item-text="bank_name"
                    :placeholder="$t('placeholder.bankName')"
                    filled
                    id="bankName"
                    class="no-input-underline rounded center-appended-icon"
                    :rules="$rules.requiredRules"
                  >
                    <template v-slot:append>
                      <v-btn fab depressed x-small color="transparent" class="disable-hover-bg" :ripple="false"><v-icon class="transparent">mdi-chevron-down</v-icon></v-btn>
                    </template>
                    <template slot="item" slot-scope="data">
                      <img width="50px" height="20px" :src="data.item.image" class="mr-4" />
                      {{ data.item.bank_name }}
                    </template>
                  </v-select>
                </fieldset>
                <fieldset>
                  <label for="form.account_type" class="font-weight-bold">{{ $t('label.bankType') }}</label>
                  <v-select
                    v-model="form.account_type"
                    :items="accountType"
                    item-value="id"
                    item-text="label"
                    :placeholder="$t('placeholder.bankType')"
                    filled
                    id="bankType"
                    class="no-input-underline rounded center-appended-icon"
                    :rules="$rules.requiredRules"
                  >
                    <template v-slot:append>
                      <v-btn fab depressed x-small color="transparent" class="disable-hover-bg" :ripple="false"><v-icon class="transparent">mdi-chevron-down</v-icon></v-btn>
                    </template>
                  </v-select>
                </fieldset>
                <fieldset>
                  <label for="form.account_no" class="font-weight-bold">{{ $t('label.bankAccountNo') }}</label>
                  <v-text-field v-model="form.account_no" filled id="bankAccountNumber" class="no-input-underline rounded center-appended-icon" :rules="$rules.requiredRules" />
                </fieldset>
                <fieldset>
                  <label for="bankAccountName" class="font-weight-bold">{{ $t('label.bankAccountHolderName') }}</label>
                  <v-text-field v-model="form.account_holder_name" filled id="bankAccountName" class="no-input-underline rounded center-appended-icon" :rules="$rules.requiredRules" />
                </fieldset>
                <fieldset>
                  <label for="form.email" class="font-weight-bold">{{ $t('label.emailAddress') }}</label>
                  <v-text-field v-model="form.email" filled id="emailAddress" class="no-input-underline rounded center-appended-icon" :rules="$rules.emailRules" />
                </fieldset>
              </div>
            </section>
          </div>
        </v-form>
      </v-card>
    </LayoutContent>
    <bottom-bar>
      <v-container class="py-3 d-flex align-center justify-center">
        <app-button :label="$t('label.convert')" @click="transfer" width="10rem" />
      </v-container>
    </bottom-bar>
  </div>
</template>

<script>
import SectionTitle from '@/components/SectionTitle.vue'
import LayoutContent from '@/views/layouts/LayoutContent.vue'
import { mapState, mapActions, mapGetters } from 'vuex'
import { localeHelper } from '@/utils'

export default {
  name: 'TransferToBank',
  components: { LayoutContent, SectionTitle },
  data: () => ({
    presetCashAmounts: [50, 100, 150, 200],
    bankList: [],
    accountType: [
      { id: 1, label: localeHelper.getMessage('label.individual') },
      { id: 2, label: localeHelper.getMessage('label.company') }
    ],
    form: {
      bank_id: null,
      account_type: null,
      account_no: null,
      account_holder_name: '',
      email: '',
      amount: 0
    }
  }),
  computed: {
    ...mapGetters('page', ['agent_min_withdrawal_amount']),
    ...mapState('agent', ['currentAgent'])
  },
  created() {
    this.getBankList().then(({ bank_list }) => {
      this.bankList = bank_list
    })
  },
  methods: {
    ...mapActions('agent', ['getBankList', 'transferToBank']),
    transfer() {
      if (!this.$refs.transferForm.validate()) {
        return true
      }
      this.transferToBank(this.form)
        .then((res) => {
          this.$router.push({ name: this.$constants.ROUTE_NAME.AGENT_TRANSFER_TO_BANK_COMPLETE })
        })
        .catch((err) => this.$toast.error(err.error_message))
    }
  }
}
</script>

<style lang="scss" scoped>
.coin-conversion--container {
  display: grid;
  grid-template-columns: 1fr;
  border-bottom: 1px solid #dedede;

  @media only screen and (min-width: 1000px) {
    grid-template-columns: 2fr 3fr 3fr;
  }
}

.coin-options--wrapper {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.coin-option {
  border-color: var(--v-primary-lighten1) !important;
}

.bank-account-details {
  max-width: 1000px;
}

.bank-fields--wrapper {
  display: grid;
  grid-template-columns: 1fr;
  column-gap: max(2rem, 7.5vw);
  @media only screen and (min-width: 1000px) {
    grid-template-columns: 1fr 1fr;
  }
}
</style>
